<template>
  <b-modal
    v-model="showModal"
    title="Payment Order Details"
    size="lg"
    @hidden="$emit('modal-closed')"
  >
    <div v-if="details">
      <b-table-simple
        responsive
        hover
        class="compact-table"
      >
        <b-tbody>
          <b-tr>
            <b-th> Order ID :</b-th>
            <b-td> {{ details.order_id || '-' }} </b-td>
            <b-th> Doc No. :</b-th>
            <b-td class="text-primary font-weight-bold">
              {{ docNumber }}
            </b-td>
            <b-th> Status :</b-th>
            <b-td>
              <app-chip :variant="getStatusChipVariant(details.status)">
                {{ capitalize(details.status) }}
              </app-chip>
            </b-td>
          </b-tr>
          <b-tr>
            <b-th> Gateway Order ID :</b-th>
            <b-td colspan="3">
              {{ details.gateway_order_id || '-' }}
            </b-td>
            <b-th> Target Type :</b-th>
            <b-td> {{ capitalize(details.target_type) }} </b-td>
          </b-tr>
          <b-tr>
            <b-th> Buyer :</b-th>
            <b-td colspan="3">
              {{ details.buyer_identifier || '-' }}
            </b-td>
            <b-th> Amount :</b-th>
            <b-td> {{ formateNumber(details.amount) }} </b-td>
          </b-tr>
          <b-tr>
            <b-th> Initiated By :</b-th>
            <b-td colspan="3">
              {{ details.initiated_by_full_name || '-' }}
              <app-chip
                class="ml-50"
                :variant="details.initiated_by_retailer ? 'info' : 'primary'"
              >
                {{ details.initiated_by_retailer ? 'Retailer' : 'Staff' }}
              </app-chip>
            </b-td>
            <b-th> Payment Method :</b-th>
            <b-td> {{ details.payment_method || '-' }} </b-td>
          </b-tr>
          <b-tr>
            <b-th> Created At :</b-th>
            <b-td colspan="3">
              {{ details.created_at && formatedDate(details.created_at, 'DD-MM-YYYY hh:mm A') || '-' }}
            </b-td>
            <b-th> Paid At :</b-th>
            <b-td>
              {{ details.paid_at && formatedDate(details.paid_at, 'DD-MM-YYYY hh:mm A') || '-' }}
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <h6 class="mt-2 mb-1">
        Documents
      </h6>
      <b-table
        :items="documentRows"
        :fields="allocationFields"
        responsive
        striped
        hover
        show-empty
        empty-text="No documents"
        class="compact-table allocation-table"
      >
        <template #cell(doc_type)="{ item: doc }">
          {{ capitalize(doc.doc_type) }}
        </template>
        <template #cell(unique_doc_no)="{ item: doc }">
          <span class="text-primary font-weight-bold">
            {{ doc.unique_doc_no || '-' }}
          </span>
        </template>
        <template #cell(amount)="{ item: doc }">
          {{ formateNumber(doc.amount) }}
        </template>
        <template #cell(reconciled_in_marg)="{ item: doc }">
          <app-chip
            v-if="doc.allocation"
            :variant="doc.allocation.reconciled_in_marg ? 'success' : 'warning'"
          >
            {{ doc.allocation.reconciled_in_marg ? 'Updated' : 'Pending' }}
          </app-chip>
          <span v-else>-</span>
        </template>
        <template #head(reconciled_by_full_name)>
          Updated By<br>In Marg
        </template>
        <template #cell(reconciled_by_full_name)="{ item: doc }">
          {{ doc.allocation && doc.allocation.reconciled_by_full_name || '-' }}
        </template>
        <template #head(reconciled_at)>
          Updated in<br>Marg At
        </template>
        <template #cell(reconciled_at)="{ item: doc }">
          {{ doc.allocation && doc.allocation.reconciled_at && formatedDate(doc.allocation.reconciled_at, 'DD-MM-YYYY hh:mm A') || '-' }}
        </template>
      </b-table>

      <b-form-group label="Remarks">
        <b-form-textarea
          :value="details.remarks"
          rows="3"
          placeholder="Remarks"
          disabled
        />
      </b-form-group>
    </div>

    <template #modal-footer="{ cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Close
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BTable, BTableSimple, BTr, BTh, BTd, BTbody, BFormTextarea, BFormGroup,
} from 'bootstrap-vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import AppChip from '@/components/UI/AppChip.vue'

export default {
  components: {
    BModal,
    BButton,
    BTable,
    BTableSimple,
    BTr,
    BTh,
    BTd,
    BTbody,
    BFormTextarea,
    BFormGroup,
    AppChip,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      showModal: true,
      allocationFields: [
        { key: 'doc_type', label: 'Doc Type' },
        { key: 'unique_doc_no', label: 'Doc No' },
        { key: 'amount', label: 'Amount', class: 'text-right pr-2' },
        { key: 'reconciled_in_marg', label: 'Marg Status' },
        { key: 'reconciled_by_full_name', label: 'Updated By In Marg' },
        { key: 'reconciled_at', label: 'Updated in Marg At' },
      ],
    }
  },
  computed: {
    details() {
      return this.item
    },
    docNumber() {
      if (this.details.target_type === 'statement') {
        return this.details.target_id || '-'
      }
      return this.details.documents?.[0]?.unique_doc_no || '-'
    },
    // Every document the order was for, with its Marg reconciliation details when
    // it has an allocation (i.e. only once the payment succeeded).
    documentRows() {
      const allocations = this.details.allocations || []
      return (this.details.documents || []).map(doc => {
        const allocation = allocations.find(
          a => a.doc_type === doc.doc_type && a.doc_id === doc.doc_id,
        )
        return { ...doc, allocation }
      })
    },
  },
  methods: {
    capitalize(value) {
      if (!value) return '-'
      return value.charAt(0).toUpperCase() + value.slice(1)
    },
    getStatusChipVariant(value) {
      if (value === 'success') return 'success'
      if (value === 'pending') return 'warning'
      if (value === 'failed') return 'danger'
      return 'secondary'
    },
  },
}
</script>

<style scoped>
td {
  text-align: start;
}
th {
  width: 55px;
  margin: 0;
  padding: 0;
}
.allocation-table ::v-deep th,
.allocation-table ::v-deep td {
  white-space: nowrap;
  width: auto;
}
</style>
